import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Package,
  RefreshCw,
  DollarSign,
  Loader2
} from 'lucide-react';
import { adminService } from '../../services/adminService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import { Plan } from '../../types';

interface PlanFormData {
  name: string;
  slug: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  price_usd_monthly: number;
  price_usd_yearly: number;
  campaigns_limit: number;
  keywords_limit: number;
  competitors_limit: number;
  content_generations_limit: number;
  automated_workflows_limit: number;
  projects_limit: number;
  team_members_limit: number;
  ai_enabled: boolean;
  api_access: boolean;
  image_studio_enabled: boolean;
  image_generations_limit: number;
  is_active: boolean;
}

const defaultFormData: PlanFormData = {
  name: '',
  slug: '',
  description: '',
  price_monthly: 0,
  price_yearly: 0,
  price_usd_monthly: 0,
  price_usd_yearly: 0,
  campaigns_limit: 5,
  keywords_limit: 50,
  competitors_limit: 3,
  content_generations_limit: 10,
  automated_workflows_limit: 5,
  projects_limit: 3,
  team_members_limit: 1,
  ai_enabled: false,
  api_access: false,
  image_studio_enabled: false,
  image_generations_limit: 0,
  is_active: true
};

const AdminPlansPage: React.FC = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const response = await adminService.getPlans();
      if (response.success && response.data) {
        // Filter out the custom plan - it's not meant to be managed here
        const filteredPlans = response.data.filter((plan: Plan) => plan.slug !== 'custom');
        setPlans(filteredPlans);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncWithStripe = async () => {
    setIsSyncing(true);
    try {
      const response = await adminService.syncPlansWithStripe();
      if (response.success) {
        toast.success(response.message || t('admin.syncSuccess', 'Plans synced with Stripe'));
        loadPlans();
      } else {
        toast.error(response.message || t('errors.generic'));
      }
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsSyncing(false);
    }
  };

  const handleOpenModal = (plan?: Plan) => {
    if (plan) {
      setIsEditing(true);
      setSelectedPlan(plan);
      setFormData({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly,
        price_usd_monthly: (plan as any).price_usd_monthly || 0,
        price_usd_yearly: (plan as any).price_usd_yearly || 0,
        campaigns_limit: plan.limits?.campaigns || plan.limits?.campaigns_limit || 5,
        keywords_limit: plan.limits?.keywords || plan.limits?.keywords_limit || 50,
        competitors_limit: plan.limits?.competitors || plan.limits?.competitors_limit || 3,
        content_generations_limit: plan.limits?.content_generations || plan.limits?.content_generations_limit || 10,
        automated_workflows_limit: (plan.limits as any)?.automated_workflows || (plan.limits as any)?.automated_workflows_limit || 5,
        projects_limit: (plan.limits as any)?.projects || (plan.limits as any)?.projects_limit || 3,
        team_members_limit: (plan.limits as any)?.team_members || (plan.limits as any)?.team_members_limit || 1,
        ai_enabled: plan.limits?.ai_enabled || false,
        api_access: plan.limits?.api_access || false,
        image_studio_enabled: (plan.limits as any)?.image_studio_enabled || false,
        image_generations_limit: (plan.limits as any)?.image_generations || (plan.limits as any)?.image_generations_limit || 0,
        is_active: plan.is_active !== false
      });
    } else {
      setIsEditing(false);
      setSelectedPlan(null);
      setFormData(defaultFormData);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
    setFormData(defaultFormData);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    setIsSaving(true);
    try {
      const planData: Partial<Plan> & { price_usd_monthly?: number; price_usd_yearly?: number } = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        price_monthly: formData.price_monthly,
        price_yearly: formData.price_yearly,
        price_usd_monthly: formData.price_usd_monthly,
        price_usd_yearly: formData.price_usd_yearly,
        is_active: formData.is_active,
        limits: {
          campaigns: formData.campaigns_limit,
          keywords: formData.keywords_limit,
          competitors: formData.competitors_limit,
          content_generations: formData.content_generations_limit,
          automated_workflows: formData.automated_workflows_limit,
          projects: formData.projects_limit,
          team_members: formData.team_members_limit,
          ai_enabled: formData.ai_enabled,
          api_access: formData.api_access,
          image_studio_enabled: formData.image_studio_enabled,
          image_generations: formData.image_generations_limit
        }
      };

      if (isEditing && selectedPlan) {
        await adminService.updatePlan(selectedPlan.id, planData);
        toast.success(t('messages.updatedSuccessfully'));
      } else {
        await adminService.createPlan(planData);
        toast.success(t('messages.createdSuccessfully'));
      }

      handleCloseModal();
      loadPlans();
    } catch (error: any) {
      toast.error(error.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm(t('messages.confirmDelete'))) return;
    try {
      await adminService.deletePlan(planId);
      toast.success(t('messages.deletedSuccessfully'));
      loadPlans();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleToggleActive = async (plan: Plan) => {
    try {
      await adminService.updatePlan(plan.id, { is_active: !plan.is_active });
      toast.success(t('messages.updatedSuccessfully'));
      loadPlans();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('admin.plans')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('admin.managePlans')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            onClick={handleSyncWithStripe}
            disabled={isSyncing}
          >
            {t('admin.syncWithStripe', 'Sync with Stripe')}
          </Button>
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>
            {t('admin.newPlan')}
          </Button>
        </div>
      </div>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">{t('common.noData')}</p>
              <Button size="sm" onClick={() => handleOpenModal()}>
                <Plus className="w-4 h-4 mr-2" />
                {t('admin.newPlan')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={!plan.is_active ? 'opacity-60' : ''}>
              <CardHeader
                action={
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleOpenModal(plan)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(plan.id)}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-600 dark:hover:bg-red-900/20 dark:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                }
              >
                <div className="flex items-center gap-2">
                  <CardTitle>{plan.name}</CardTitle>
                  {!plan.is_active && (
                    <Badge variant="gray">{t('admin.inactive')}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{plan.description}</p>

                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 dark:text-gray-400">🇧🇷</span>
                      <span className="text-gray-600 dark:text-gray-400">{t('admin.monthly')}</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white text-right">
                      {plan.price_monthly.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      })}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 dark:text-gray-400">🇧🇷</span>
                      <span className="text-gray-600 dark:text-gray-400">{t('admin.yearly')}</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white text-right">
                      {plan.price_yearly.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      })}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 dark:text-gray-400">🇺🇸</span>
                      <span className="text-gray-600 dark:text-gray-400">{t('admin.monthly')}</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white text-right">
                      {((plan as any).price_usd_monthly || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 dark:text-gray-400">🇺🇸</span>
                      <span className="text-gray-600 dark:text-gray-400">{t('admin.yearly')}</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white text-right">
                      {((plan as any).price_usd_yearly || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })}
                    </span>
                  </div>
                  {/* Stripe sync status */}
                  <div className="flex items-center gap-2 text-xs">
                    {(plan as any).stripe_product_id ? (
                      <Badge variant="success">{t('admin.stripeSynced', 'Stripe Synced')}</Badge>
                    ) : (
                      <Badge variant="warning">{t('admin.notSynced', 'Not Synced')}</Badge>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.projects', 'Projects')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.projects ?? (plan.limits as any)?.projects_limit) === -1 ? '∞' : ((plan.limits as any)?.projects ?? (plan.limits as any)?.projects_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.campaigns')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.campaigns ?? (plan.limits as any)?.campaigns_limit) === -1 ? '∞' : ((plan.limits as any)?.campaigns ?? (plan.limits as any)?.campaigns_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.keywords')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.keywords ?? (plan.limits as any)?.keywords_limit) === -1 ? '∞' : ((plan.limits as any)?.keywords ?? (plan.limits as any)?.keywords_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.competitors')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.competitors ?? (plan.limits as any)?.competitors_limit) === -1 ? '∞' : ((plan.limits as any)?.competitors ?? (plan.limits as any)?.competitors_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.contentGenerations')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.content_generations ?? (plan.limits as any)?.content_generations_limit) === -1 ? '∞' : ((plan.limits as any)?.content_generations ?? (plan.limits as any)?.content_generations_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.automatedWorkflows')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.automated_workflows ?? (plan.limits as any)?.automated_workflows_limit) === -1 ? '∞' : ((plan.limits as any)?.automated_workflows ?? (plan.limits as any)?.automated_workflows_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.teamMembers')}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{((plan.limits as any)?.team_members ?? (plan.limits as any)?.team_members_limit) === -1 ? '∞' : ((plan.limits as any)?.team_members ?? (plan.limits as any)?.team_members_limit ?? 0)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.aiEnabled')}</span>
                    {plan.limits?.ai_enabled ? (
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.apiAccess')}</span>
                    {plan.limits?.api_access ? (
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('admin.enableImageStudio', 'Image Studio')}</span>
                    {(plan.limits as any)?.image_studio_enabled ? (
                      <span className="flex items-center gap-1">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({(plan.limits as any)?.image_generations === -1 ? '∞' : ((plan.limits as any)?.image_generations ?? 0)} gen.)
                        </span>
                      </span>
                    ) : (
                      <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant={plan.is_active ? 'ghost' : 'secondary'}
                    size="sm"
                    className="w-full"
                    onClick={() => handleToggleActive(plan)}
                  >
                    {plan.is_active ? t('admin.deactivate') : t('admin.activate')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Plan Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={isEditing ? t('admin.editPlan') : t('admin.newPlan')}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('admin.planName')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Professional"
            />
            <Input
              label={t('admin.slug')}
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="Ex: professional"
              disabled={isEditing}
            />
          </div>

          <Input
            label={t('common.description')}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('admin.planDescription')}
          />

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-lg">🇧🇷</span> {t('admin.brlPrices', 'BRL Prices')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('admin.priceMonthly')}
                type="number"
                value={formData.price_monthly}
                onChange={(e) => setFormData({ ...formData, price_monthly: parseFloat(e.target.value) || 0 })}
                step="0.01"
              />
              <Input
                label={t('admin.priceYearly')}
                type="number"
                value={formData.price_yearly}
                onChange={(e) => setFormData({ ...formData, price_yearly: parseFloat(e.target.value) || 0 })}
                step="0.01"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-lg">🇺🇸</span> {t('admin.usdPrices', 'USD Prices')}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('admin.priceMonthly')}
                type="number"
                value={formData.price_usd_monthly}
                onChange={(e) => setFormData({ ...formData, price_usd_monthly: parseFloat(e.target.value) || 0 })}
                step="0.01"
              />
              <Input
                label={t('admin.priceYearly')}
                type="number"
                value={formData.price_usd_yearly}
                onChange={(e) => setFormData({ ...formData, price_usd_yearly: parseFloat(e.target.value) || 0 })}
                step="0.01"
              />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">{t('admin.planLimits')}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('admin.limitsHint', 'Use -1 for unlimited')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('admin.projectsLimit', 'Projects Limit')}
                type="number"
                value={formData.projects_limit}
                onChange={(e) => setFormData({ ...formData, projects_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.campaignsLimit')}
                type="number"
                value={formData.campaigns_limit}
                onChange={(e) => setFormData({ ...formData, campaigns_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.keywordsLimit')}
                type="number"
                value={formData.keywords_limit}
                onChange={(e) => setFormData({ ...formData, keywords_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.competitorsLimit')}
                type="number"
                value={formData.competitors_limit}
                onChange={(e) => setFormData({ ...formData, competitors_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.contentGenerationsLimit')}
                type="number"
                value={formData.content_generations_limit}
                onChange={(e) => setFormData({ ...formData, content_generations_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.automatedWorkflowsLimit')}
                type="number"
                value={formData.automated_workflows_limit}
                onChange={(e) => setFormData({ ...formData, automated_workflows_limit: parseInt(e.target.value) || 0 })}
              />
              <Input
                label={t('admin.teamMembersLimit')}
                type="number"
                value={formData.team_members_limit}
                onChange={(e) => setFormData({ ...formData, team_members_limit: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.ai_enabled}
                onChange={(e) => setFormData({ ...formData, ai_enabled: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('admin.enableAI')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.api_access}
                onChange={(e) => setFormData({ ...formData, api_access: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('admin.enableAPI')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.image_studio_enabled}
                onChange={(e) => setFormData({ ...formData, image_studio_enabled: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('admin.enableImageStudio', 'Image Studio')}</span>
            </label>
            {formData.image_studio_enabled && (
              <div className="ml-7">
                <Input
                  label={t('admin.imageGenerationsLimit', 'Image Generations Limit (-1 = unlimited)')}
                  type="number"
                  value={formData.image_generations_limit}
                  onChange={(e) => setFormData({ ...formData, image_generations_limit: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('admin.planActive')}</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={handleCloseModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} isLoading={isSaving}>
              {isEditing ? t('common.save') : t('common.create')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPlansPage;
